import type { GetSymbolsResponse, MarketLayoutNode } from '@flipster/apis/v2/market/types';
import { InstrumentProductTypeEnum } from '@flipster/apis/v2/snapshot';
import { flattenMarketLayout, getSymbolsByDataRef } from '@flipster/features/src/shared/market-layout';
import { getQuoteCurrencyDataRef } from '@flipster/features/src/shared/quote-currency';
import { TradFiCategory, getTradFiCategorySymbols } from '@flipster/features/src/shared/rwa';
import { InstrumentV2ForApiInstanceType } from '@flipster/features/src/shared/snapshot/utils';
import { getTickerDisplayPriceOrDefault, type TickerPriceSource } from '@flipster/r230522/original/ticker-price';
import { QuoteCurrencyListExceptUsdt } from '@flipster/utils/constants/currency';
import { sortingHelper } from '@flipster/utils/sort';

import { MarketPageTabType, SearchTabType, Sort, TabValueType, TableItem } from './types';

const emptySort: Sort = { sortOrder: undefined, sortKey: undefined };

export const getDefaultSortForTab = (tab: TabValueType, marketTab: MarketPageTabType): Sort =>
  tab === 'tradfi' || (marketTab === 'perpetual' && tab === 'marketCap')
    ? { sortKey: 'turnover24h', sortOrder: 'desc' }
    : emptySort;

// Market trend(1h) 위젯을 눌렀을 때 이동할 discover 탭.
// 예전에는 'priceChange24h'(Top movers) 탭으로 보냈지만, discover 탭이 서버 layout 기반으로
// 재구성되면서(#6030) 그 탭이 사라졌다. 없는 탭으로 보내면 dataRef 를 못 찾아 빈 리스트가 된다(QA-21314).
// layout 에 항상 존재하는 전체 종목 탭(marketCap)으로 보내고 정렬만 24h 변동률로 바꾼다.
export const marketTrendDiscoverTab: TabValueType = 'marketCap';

// 상승 클릭 → 많이 오른 순, 하락 클릭 → 많이 내린 순.
export const getMarketTrendSort = (trend: 'up' | 'down'): Sort => ({
  sortKey: 'priceChangePct24h',
  sortOrder: trend === 'up' ? 'desc' : 'asc',
});

// 검색 스코프는 규제로 결정한다. VARA(spot-only)는 spot 만, 그 외는 perpetual·tradfi·spot.
export const resolveSearchTabs = (isSpotOnly: boolean): SearchTabType[] =>
  isSpotOnly ? ['spot'] : ['perpetual', 'tradfi', 'spot'];

// 활성 검색 탭은 항상 사용 가능한 스코프 안에 있어야 한다.
// 현재 커서 탭이 스코프에 없으면(예: VARA 유저에게 남아있는 'perpetual') 첫 번째 탭으로 떨어진다.
export const resolveSearchTab = (searchTabs: SearchTabType[], current: SearchTabType): SearchTabType =>
  searchTabs.includes(current) ? current : searchTabs[0];

// 탭이 조회할 심볼 API 의 productType 을 결정한다.
// spot 만 Spot, perpetual·tradfi 는 Pswap (TradFi 는 perpetual 의 부분집합이라 perp 응답을 받아 필터링한다).
export const getProductTypeForTab = (
  tab: SearchTabType,
): (typeof InstrumentProductTypeEnum)[keyof typeof InstrumentProductTypeEnum] =>
  tab === 'spot' ? InstrumentProductTypeEnum.Spot : InstrumentProductTypeEnum.Pswap;

export const isDiscoverTabHidden = (
  value: TabValueType,
  dataRef: string | undefined,
  symbols: GetSymbolsResponse | undefined,
  isLoggedIn: boolean,
): boolean => {
  if (value === 'favorites') return !isLoggedIn;
  if (value === 'tradfi') return getTradFiCategorySymbols(symbols, 'all').length === 0;
  if ((QuoteCurrencyListExceptUsdt as readonly string[]).includes(value)) {
    return getSymbolsByDataRef(symbols?.data, dataRef).length === 0;
  }
  return false;
};

export interface DiscoverTab {
  value: TabValueType;
  dataRef?: string;
}

const tabValueByNodeType: Partial<Record<string, TabValueType>> = {
  SECTION_TYPE_FAVORITES: 'favorites',
  SECTION_TYPE_TRADFI: 'tradfi',
  COMPONENT_TYPE_ALL_TOKENS: 'marketCap',
};
const tabValueByDataRef: Partial<Record<string, TabValueType>> = {
  volume: 'marketCap',
  newListings: 'newListings',
  hotTradings: 'hotTradings',
  absFundingRates: 'absFundingRates',
  // USDT 외 quote currency 탭: dataRef(소문자 통화 코드) → 탭 id(통화 코드).
  // 큐레이션 목록에 통화를 추가하면 탭이 자동으로 잡힌다.
  ...Object.fromEntries(
    QuoteCurrencyListExceptUsdt.map((currency) => [getQuoteCurrencyDataRef(currency), currency] as const),
  ),
};

// 서버 layout 을 순회하며 웹 discover 탭 목록(서버 순서·중복 제거)을 만든다.
export const getDiscoverTabs = (nodes: MarketLayoutNode[] | undefined): DiscoverTab[] => {
  const tabs: DiscoverTab[] = [];
  const seen = new Set<TabValueType>();
  for (const { type, dataRef } of flattenMarketLayout(nodes)) {
    const value = tabValueByNodeType[type] ?? (dataRef ? tabValueByDataRef[dataRef] : undefined);
    if (value && !seen.has(value)) {
      seen.add(value);
      tabs.push({ value, dataRef });
    }
  }
  return tabs;
};

// 검색 풀: tradfi 검색은 TradFi 심볼만, 그 외(perpetual·spot)는 해당 상품군 전체(marketCap)를 검색한다.
export const resolveSearchPool = (searchTab: SearchTabType, symbols: GetSymbolsResponse): string[] =>
  searchTab === 'tradfi' ? getTradFiCategorySymbols(symbols, 'all') : symbols.data.marketCap;

// discover 탭이 보여줄 후보 심볼을 결정한다.
// favorites → 즐겨찾기, tradfi → 선택된 카테고리, 그 외 → 탭의 dataRef 목록.
export const resolveCandidateSymbols = (params: {
  tab: TabValueType;
  favoriteSymbols: string[];
  tradfiCategory: TradFiCategory;
  dataRef: string | undefined;
  symbols: GetSymbolsResponse | undefined;
}): string[] => {
  const { tab, favoriteSymbols, tradfiCategory, dataRef, symbols } = params;
  if (tab === 'favorites') return [...favoriteSymbols];
  if (tab === 'tradfi') return getTradFiCategorySymbols(symbols, tradfiCategory);
  return getSymbolsByDataRef(symbols?.data, dataRef);
};

export type MarketTicker = TickerPriceSource & {
  turnover24h?: string;
  priceChangePct24h?: string;
  fundingRate?: string;
};

type InstrumentMap = Record<string, InstrumentV2ForApiInstanceType> | undefined;

export const filterFavoriteSymbols = (favorites: string[], instruments: InstrumentMap, isSpotTab: boolean): string[] =>
  favorites.filter((symbol) => {
    const instrument = instruments?.[symbol];
    if (!instrument) return false;
    return instrument.isSpot === isSpotTab;
  });

export const toTableItems = (
  markets: Record<string, MarketTicker | undefined>,
  symbols: string[],
  instruments: InstrumentMap,
): TableItem[] =>
  symbols.map((symbol) => {
    const market = markets[symbol];
    return {
      symbol,
      turnover24h: market?.turnover24h || '0',
      displayPrice: getTickerDisplayPriceOrDefault(market),
      priceChangePct24h: market?.priceChangePct24h || '0',
      fundingRate: market?.fundingRate || '0',
      leverage: instruments?.[symbol]?.maxLeverageHardLimit,
    };
  });

export const sortTableItems = (
  items: TableItem[] | undefined,
  sort: Sort,
  instruments: InstrumentMap,
): TableItem[] | undefined => {
  if (!items || !sort.sortKey) return items;
  if (sort.sortKey === 'symbol') {
    if (!instruments) return items;
    return [...items].sort((a, b) =>
      sortingHelper(instruments[a.symbol]?.symbol, instruments[b.symbol]?.symbol, {
        type: 'string',
        order: sort.sortOrder!,
      }),
    );
  }
  if (sort.sortKey === 'leverage') {
    return [...items].sort((a, b) =>
      sortingHelper(a.leverage ?? 0, b.leverage ?? 0, { type: 'number', order: sort.sortOrder! }),
    );
  }
  const sortKey = sort.sortKey;
  return [...items].sort((a, b) =>
    sortingHelper(a[sortKey], b[sortKey], { type: 'bignumber', order: sort.sortOrder! }),
  );
};
